<template lang="pug">
  div(class="templates-dynamic-fields")
    templates-dynamic-field(
      v-for="(field, index) in sortedFields"
      :key="field.id"
      ref="dynamicFields"
      :field="field"
      :data-test="`template-field-${field.type}-${index}`"
      :can-edit="canEdit"
      :is-template="isTemplatesType"
      :is-disabled="isDisabledFields"
      :step="currentStep"
      :steps-count="stepsCount"
      :show-raw-values="showRawValues"
      @startDrag="setFieldDragData"
      @duplicate="pasteField"
      @copy="copyField"
      @cut="cutField"
      @delete="deleteField"
      :isLargeDocument="isLargeDocument"
    )
</template>

<script>
import { mapGetters } from 'vuex'
import documentActions from '~/modules/documents/mixins/documentActions'
import documentViewTypes from '~/modules/documents/mixins/documentViewTypes'
import TemplatesDynamicField from '~/modules/templates/components/inputs/templates-dynamic-field'
import { STATUSES } from '~/modules/templates/constants/fields'
import { PARTICIPANTS_ROLES } from '~/modules/documents/constants/participants'

export default {
  name: 'TemplatesDynamicFieldsWrapper',
  components: {
    TemplatesDynamicField
  },
  mixins: [documentActions, documentViewTypes],
  props: {
    item: {
      type: Object,
      default: () => ({})
    },
    type: {
      type: String,
      required: true,
      validator: value => ['documents', 'templates'].includes(value)
    },
    pageNumber: {
      type: Number,
      default: null
    },
    stepsCount: {
      type: Number,
      default: null
    },
    currentStep: {
      type: Number,
      default: null
    },
    showRawValues: {
      type: Boolean,
      default: false
    },
    isPublicTemplate: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('templatefields', {
      fields: 'fields',
      isDraggingMap: 'isDraggingMap',
      toolbarsMap: 'toolbarsMap'
    }),
    ...mapGetters('documents', {
      documentScale: 'documentScale'
    }),
    visibleTemplateFields() {
      return this._.filter(
        this.fields,
        field => field.status !== STATUSES.to_delete && field.page === this.pageNumber
      )
    },
    canEdit() {
      return this.isTemplatesType && !this.isPublicTemplate
    },
    formattedFields() {
      return this._.map(this.visibleTemplateFields, field => ({
        ...field,
        hint: this.$t(field.hint)
      }))
    },
    sortedFields() {
      const sortedFields = this._.sortBy(this.formattedFields, [f => f.positionY])
      if (this.isDocumentsType) {
        return this._.filter(sortedFields, field => !field.documentFieldValue)
      }
      return sortedFields
    },
    isDisabledFields() {
      if (this.isDocumentsType) {
        return this._.get(this.item, 'currentUser.role') === PARTICIPANTS_ROLES.VIEWER
      }
      return false
    },
    isLargeDocument() {
      return this.documentScale < 1
    }
  },
  watch: {
    fields(val) {
      if (this.isTemplatesType) {
        this._.forEach(val, field => {
          if (!(field.id in this.isDraggingMap)) {
            this.$store.commit('templatefields/SET_DRAGGING_MAP_FIELD_ID', {
              id: field.id,
              value: false
            })
            this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
              id: field.id,
              value: false
            })
          }
        })
      }
    }
  },
  beforeDestroy() {
    this.cleanLocalTemplateFields()
    this.$store.commit('templatefields/RESET_FILLED_FIELDS')
  },
  methods: {
    cleanLocalTemplateFields() {
      this.$store.commit('templatefields/RESET_FIELDS')
    },
    setFieldDragData(offsetX, offsetY) {
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_OFFSET_X', offsetX)
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_OFFSET_Y', offsetY)
    },
    copyField(field) {
      this.$store.commit('templatefields/SET_CUTTED_FIELD', null)
      this.$store.commit('templatefields/SET_COPIED_FIELD', field)
    },
    cutField(field) {
      this.$store.commit('templatefields/SET_COPIED_FIELD', null)
      if (field.status === STATUSES.created) {
        this.$store.commit('templatefields/SET_FIELD_TO_DELETE', {
          ...field,
          status: STATUSES.to_delete
        })
      }
      this.$store.commit('templatefields/SET_CUTTED_FIELD', {
        ...field,
        status: STATUSES.to_delete
      })
      this.$store.commit('templatefields/DELETE_FIELD', field.id)
    },
    pasteField(field) {
      this.$store.dispatch('templatefields/pasteField', {
        field,
        documentScale: this.documentScale
      })
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: field.id,
        value: false
      })
    },
    deleteField(field) {
      if (field.status === STATUSES.created) {
        this.$store.commit('templatefields/SET_FIELD_TO_DELETE', {
          ...field,
          status: STATUSES.to_delete
        })
      }
      this.$store.commit('templatefields/DELETE_FIELD', field.id)
      this.cleanupFieldMaps(field.id)
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', null)
    },
    cleanupFieldMaps(fieldId) {
      this.$store.commit('templatefields/REMOVE_DRAGGING_MAP_FIELD', fieldId)
      this.$store.commit('templatefields/REMOVE_TOOLBARS_MAP_FIELD', fieldId)
    }
  }
}
</script>

<style scoped lang="scss"></style>
